// packages/dealer-portal/src/pages/RewardsPage.tsx
// Dark theme for dealer portal

import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Inline SVG Icons
const TrophyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const AcademicCapIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path d="M12 14l9-5-9-5-9 5 9 5z" />
    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
  </svg>
);

const ChartBarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface RewardsDashboard {
  currentPoints: number;
  lifetimePoints: number;
  ytdPointsEarned: number;
  tier: {
    name: string;
    displayName: string;
    discountPercent: number;
    color: string;
    icon: string;
    perks: string[];
    minPoints: number;
    maxPoints: number | null;
  };
  nextTier: {
    name: string;
    displayName: string;
    minPoints: number;
    discountPercent: number;
    pointsNeeded: number;
  } | null;
  progress: {
    tierProgress: number;
    pointsToNextTier: number | null;
  };
  leaderboard: {
    tier_rank: number | null;
    tier_total: number | null;
    global_rank: number | null;
  };
  monthlyEarnings: Array<{ transaction_type: string; total_points: string; count: string }>;
  recentTransactions: Array<any>;
  training: { completed: number; total: number };
}

interface TrainingModule {
  id: string;
  name: string;
  description: string;
  wordpressUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  durationMinutes: number | null;
  pointsAwarded: number;
  category: string;
  isCompleted: boolean;
  completedAt: string | null;
  earnedPoints: number | null;
  completedByName: string | null;
  isVerified: boolean;
  videoWatchedAt: string | null;
  lastAttemptAt: string | null;
  bestScorePercent: number;
  quizPointsEarned: number;
  attemptCount: number;
  questionCount: number;
}

export default function RewardsPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'training' | 'leaderboard' | 'history'>('overview');
  const [dashboard, setDashboard] = useState<RewardsDashboard | null>(null);
  const [training, setTraining] = useState<TrainingModule[]>([]);
  const [leaderboard, setLeaderboard] = useState<any>(null);
  const [leaderboardScope, setLeaderboardScope] = useState<'tier' | 'global'>('tier');
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Quiz modal state
  const [quizModule, setQuizModule] = useState<TrainingModule | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<any>(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [videoWatching, setVideoWatching] = useState<TrainingModule | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'training' && training.length === 0) loadTraining();
    if (activeTab === 'leaderboard') loadLeaderboard();
    if (activeTab === 'history' && history.length === 0) loadHistory();
  }, [activeTab, leaderboardScope]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rewards/my-rewards');
      setDashboard(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load rewards');
    } finally {
      setLoading(false);
    }
  };

  const loadTraining = async () => {
    try {
      const response = await api.get('/rewards/training');
      setTraining(response.data);
    } catch (err) {
      console.error('Failed to load training:', err);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await api.get(`/rewards/leaderboard?scope=${leaderboardScope}`);
      setLeaderboard(response.data);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.get('/rewards/my-history?limit=100');
      setHistory(response.data.transactions || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const watchVideo = async (mod: TrainingModule) => {
    setVideoWatching(mod);
    try {
      await api.post(`/rewards/training/${mod.id}/video-watched`);
    } catch {}
  };

  const startQuiz = async (mod: TrainingModule) => {
    setQuizLoading(true);
    setQuizModule(mod);
    setQuizResult(null);
    setQuizAnswers({});
    setQuizIndex(0);
    try {
      const response = await api.get(`/rewards/training/${mod.id}/quiz`);
      setQuizQuestions(response.data || []);
    } catch (err: any) {
      setError('Failed to load quiz');
      setQuizModule(null);
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (!quizModule) return;
    setQuizSubmitting(true);
    try {
      const answers = quizQuestions.map(q => ({ questionId: q.id, answer: quizAnswers[q.id] || '' }));
      const response = await api.post(`/rewards/training/${quizModule.id}/submit-quiz`, { answers });
      setQuizResult(response.data);
      // Refresh training data
      loadTraining();
      loadDashboard();
    } catch (err: any) {
      setError('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="p-6 text-center text-red-400">
        <p>{error || 'Failed to load rewards dashboard'}</p>
        <button onClick={loadDashboard} className="mt-4 px-4 py-2 bg-yellow-500 text-black rounded-lg">
          Retry
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <ChartBarIcon className="w-5 h-5" /> },
    { id: 'training', label: 'Training', icon: <AcademicCapIcon className="w-5 h-5" /> },
    { id: 'leaderboard', label: 'Leaderboard', icon: <TrophyIcon className="w-5 h-5" /> },
    { id: 'history', label: 'History', icon: <ClockIcon className="w-5 h-5" /> },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with Tier Badge */}
      <div 
        className="rounded-xl p-6 mb-6 border"
        style={{ 
          background: `linear-gradient(135deg, ${dashboard.tier.color}15, ${dashboard.tier.color}05)`,
          borderColor: dashboard.tier.color + '40'
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div 
              className="text-6xl p-4 rounded-full"
              style={{ backgroundColor: dashboard.tier.color + '20' }}
            >
              {dashboard.tier.icon}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{dashboard.tier.displayName}</h1>
              <p className="text-gray-400 text-lg">
                {dashboard.tier.discountPercent}% Dealer Discount
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold text-yellow-400">
              {dashboard.currentPoints.toLocaleString()}
            </div>
            <div className="text-gray-400 text-lg">Current Points</div>
          </div>
        </div>

        {/* Progress to next tier */}
        {dashboard.nextTier && (
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progress to {dashboard.nextTier.displayName}</span>
              <span className="text-yellow-400 font-semibold">
                {dashboard.progress.pointsToNextTier?.toLocaleString()} points to go
              </span>
            </div>
            <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{ 
                  width: `${dashboard.progress.tierProgress}%`,
                  background: `linear-gradient(90deg, ${dashboard.tier.color}, #FFD700)`
                }}
              />
            </div>
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>{dashboard.tier.minPoints.toLocaleString()} pts</span>
              <span>{dashboard.nextTier.minPoints.toLocaleString()} pts</span>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{dashboard.ytdPointsEarned.toLocaleString()}</div>
            <div className="text-gray-400 text-sm">YTD Points Earned</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{dashboard.lifetimePoints.toLocaleString()}</div>
            <div className="text-gray-400 text-sm">Lifetime Points</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">
              #{dashboard.leaderboard.tier_rank || '—'}
            </div>
            <div className="text-gray-400 text-sm">
              of {dashboard.leaderboard.tier_total || '—'} in tier
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">
              {dashboard.training.completed}/{dashboard.training.total}
            </div>
            <div className="text-gray-400 text-sm">Training Complete</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-800 rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors flex-1 justify-center ${
              activeTab === tab.id 
                ? 'bg-yellow-500 text-black' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.id === 'training' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-600/30 text-yellow-400 font-semibold">Soon</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tier Perks */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>{dashboard.tier.icon}</span>
              Your {dashboard.tier.name.charAt(0).toUpperCase() + dashboard.tier.name.slice(1)} Perks
            </h3>
            <ul className="space-y-3">
              {(dashboard.tier.perks || []).map((perk, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-300">
                  <span className="text-green-400 text-lg">✓</span>
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          {/* This Month's Earnings */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">This Month's Earnings</h3>
            {dashboard.monthlyEarnings.length > 0 ? (
              <div className="space-y-3">
                {dashboard.monthlyEarnings.map((earning, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-0">
                    <div>
                      <span className="text-gray-300 capitalize">
                        {earning.transaction_type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        ({earning.count} {parseInt(earning.count) === 1 ? 'time' : 'times'})
                      </span>
                    </div>
                    <span className="text-green-400 font-semibold">+{parseInt(earning.total_points).toLocaleString()} pts</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400">No points earned this month yet.</p>
                <p className="text-gray-500 text-sm mt-2">Place an order or complete training to earn points!</p>
              </div>
            )}
          </div>

          {/* How to Earn Points */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">How to Earn Points</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">📦 Place Orders</span>
                <span className="text-yellow-400">$100 = 10-15 pts*</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">🎓 Complete Training</span>
                <span className="text-yellow-400">75-1000 pts</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">✅ Register Warranties</span>
                <span className="text-yellow-400">25 pts each</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">🤝 Refer Dealers</span>
                <span className="text-yellow-400">500 pts</span>
              </div>
              <p className="text-gray-500 text-xs mt-4">*Points vary by product margin. Higher-margin products earn more points!</p>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {dashboard.recentTransactions.slice(0, 8).map((tx, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-0">
                  <div>
                    <div className="text-white text-sm">{tx.description}</div>
                    <div className="text-gray-500 text-xs">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`font-semibold ${tx.points > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </div>
                </div>
              ))}
              {dashboard.recentTransactions.length === 0 && (
                <p className="text-gray-400 text-center py-4">No activity yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'training' && (
        <div>
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-white">Training Library</h2>
            </div>
            <p className="text-gray-400">Complete training modules to earn points. Watch the video, then take the quiz to earn your reward.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {training.map((module) => {
              const hasVideo = !!module.videoUrl;
              const hasQuiz = module.questionCount > 0;
              const maxedOut = module.quizPointsEarned >= module.pointsAwarded;
              const canTakeQuiz = hasVideo && module.videoWatchedAt && hasQuiz &&
                !(module.attemptCount > 0 && module.lastAttemptAt && module.videoWatchedAt && new Date(module.lastAttemptAt) >= new Date(module.videoWatchedAt));

              return (
                <div
                  key={module.id}
                  className={`bg-gray-800 rounded-xl p-6 border transition-all ${
                    maxedOut
                      ? 'border-green-600 bg-green-900/10'
                      : 'border-gray-700 hover:border-yellow-500/50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">{module.name}</h3>
                      <p className="text-gray-400 text-sm mt-1">{module.description}</p>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      <div className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-semibold">
                        +{module.pointsAwarded} pts
                      </div>
                      {module.quizPointsEarned > 0 && (
                        <div className="text-green-400 text-xs mt-1">{module.quizPointsEarned}/{module.pointsAwarded} earned</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                    <span className="px-2 py-0.5 bg-gray-700 rounded capitalize">{module.category}</span>
                    {module.durationMinutes && <span>{module.durationMinutes} min</span>}
                    {hasQuiz && <span>{module.questionCount} questions</span>}
                  </div>

                  {maxedOut ? (
                    <div className="flex items-center gap-2 text-green-400 bg-green-900/30 px-4 py-2 rounded-lg">
                      <span className="text-xl">✓</span>
                      <div>
                        <span className="font-medium">Complete — {module.bestScorePercent}%</span>
                        <span className="text-green-300 text-sm ml-2">{module.pointsAwarded} pts earned</span>
                      </div>
                    </div>
                  ) : !hasVideo ? (
                    <div className="bg-gray-700/50 text-gray-400 text-center py-3 rounded-lg text-sm">
                      Coming Soon
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        if (!module.videoWatchedAt) {
                          watchVideo(module);
                        } else if (canTakeQuiz) {
                          startQuiz(module);
                        } else {
                          watchVideo(module);
                        }
                      }}
                      className="w-full bg-blue-600/20 text-blue-400 text-center py-3 rounded-lg text-sm font-medium hover:bg-blue-600/30 transition-colors cursor-pointer"
                    >
                      {!module.videoWatchedAt ? 'Watch Video to Unlock Quiz' :
                       canTakeQuiz ? 'Take Quiz' :
                       `Score: ${module.bestScorePercent}% — Rewatch to Retry`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Video Modal */}
          {videoWatching && videoWatching.videoUrl && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setVideoWatching(null); loadTraining(); }}>
              <div className="bg-gray-900 rounded-xl max-w-3xl w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">{videoWatching.name}</h3>
                  <button onClick={() => { setVideoWatching(null); loadTraining(); }} className="text-gray-400 hover:text-white text-2xl">&times;</button>
                </div>
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <iframe
                    src={videoWatching.videoUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <p className="text-gray-400 text-sm">Watch the full video to unlock the quiz.</p>
                  <button
                    onClick={() => { setVideoWatching(null); loadTraining(); }}
                    className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-600"
                  >
                    Done Watching
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quiz Modal */}
          {quizModule && !quizResult && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <div className="bg-gray-900 rounded-xl max-w-lg w-full p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">{quizModule.name} — Quiz</h3>
                  <button onClick={() => setQuizModule(null)} className="text-gray-400 hover:text-white text-2xl">&times;</button>
                </div>

                {quizLoading ? (
                  <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" /></div>
                ) : quizQuestions.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No questions available.</p>
                ) : (
                  <>
                    {/* Progress */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full transition-all" style={{ width: `${((quizIndex + 1) / quizQuestions.length) * 100}%` }} />
                      </div>
                      <span className="text-gray-400 text-sm">{quizIndex + 1}/{quizQuestions.length}</span>
                    </div>

                    {/* Question */}
                    <div className="mb-6">
                      <p className="text-white font-medium mb-4">{quizQuestions[quizIndex].question}</p>
                      <div className="space-y-2">
                        {(quizQuestions[quizIndex].options || []).map((opt: string, i: number) => (
                          <button
                            key={i}
                            onClick={() => setQuizAnswers({ ...quizAnswers, [quizQuestions[quizIndex].id]: opt })}
                            className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                              quizAnswers[quizQuestions[quizIndex].id] === opt
                                ? 'border-yellow-500 bg-yellow-500/20 text-white'
                                : 'border-gray-700 text-gray-300 hover:border-gray-500'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between">
                      <div />
                      {quizIndex < quizQuestions.length - 1 ? (
                        <button
                          onClick={() => setQuizIndex(quizIndex + 1)}
                          disabled={!quizAnswers[quizQuestions[quizIndex].id]}
                          className="px-6 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                        >
                          Next
                        </button>
                      ) : (
                        <button
                          onClick={submitQuiz}
                          disabled={quizSubmitting || !quizAnswers[quizQuestions[quizIndex].id]}
                          className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {quizSubmitting ? 'Submitting...' : 'Submit Quiz'}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Quiz Result Modal */}
          {quizResult && quizModule && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <div className="bg-gray-900 rounded-xl max-w-md w-full p-6 text-center">
                <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl ${
                  quizResult.passed ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
                }`}>
                  {quizResult.passed ? '✓' : '✗'}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {quizResult.passed ? 'Congratulations!' : 'Not Quite'}
                </h3>
                <p className="text-gray-400 mb-4">
                  You scored <span className="text-white font-bold">{quizResult.scorePercent}%</span>
                  {quizResult.passed
                    ? ` and earned ${quizResult.pointsEarned} points!`
                    : '. Rewatch the video to try again.'}
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  {quizResult.correct}/{quizResult.total} correct answers
                </p>
                <button
                  onClick={() => { setQuizModule(null); setQuizResult(null); }}
                  className="px-6 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-600"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white">
                {leaderboardScope === 'tier' ? `${dashboard.tier.displayName} Leaderboard` : 'Global Leaderboard'}
              </h2>
              {leaderboard?.currentDealerPosition && (
                <p className="text-gray-400">
                  Your Rank: <span className="text-yellow-400 font-bold">#{leaderboard.currentDealerPosition.tier_rank}</span>
                  {' '}of {leaderboard.currentDealerPosition.tier_total} dealers
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setLeaderboardScope('tier')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  leaderboardScope === 'tier' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                My Tier
              </button>
              <button
                onClick={() => setLeaderboardScope('global')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  leaderboardScope === 'global' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Global
              </button>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {leaderboard?.rankings?.map((dealer: any, index: number) => (
              <div 
                key={dealer.dealer_id}
                className={`flex items-center justify-between p-4 border-b border-gray-700 last:border-0 ${
                  dealer.is_current_dealer 
                    ? 'bg-yellow-500/10' 
                    : index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                    index === 0 ? 'bg-yellow-500 text-black' :
                    index === 1 ? 'bg-gray-400 text-black' :
                    index === 2 ? 'bg-orange-600 text-white' :
                    'bg-gray-600 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-white font-medium">
                      {dealer.is_current_dealer && <span className="text-yellow-400">▶ </span>}
                      {dealer.is_current_dealer ? dealer.company_name : `Dealer #${index + 1}`}
                      {dealer.is_current_dealer && <span className="text-yellow-400"> (You)</span>}
                    </div>
                    {dealer.tier_display && (
                      <div className="text-sm text-gray-400">{dealer.tier_display}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-yellow-400 font-bold text-lg">
                    {dealer.current_points.toLocaleString()}
                  </div>
                  <div className="text-gray-500 text-sm">points</div>
                </div>
              </div>
            ))}
            {(!leaderboard?.rankings || leaderboard.rankings.length === 0) && (
              <div className="p-8 text-center text-gray-400">
                No dealers in leaderboard yet
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-6">Point History</h2>
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto">
              {history.map((tx: any) => (
                <div key={tx.id} className="flex justify-between items-center p-4 border-b border-gray-700 last:border-0 hover:bg-gray-750">
                  <div>
                    <div className="text-white">{tx.description}</div>
                    <div className="text-gray-500 text-sm">
                      {new Date(tx.created_at).toLocaleString()} • 
                      <span className="capitalize ml-1">{tx.transaction_type.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold text-lg ${tx.points > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tx.points > 0 ? '+' : ''}{tx.points}
                    </div>
                    <div className="text-gray-500 text-sm">
                      Balance: {tx.balance_after.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
              {history.length === 0 && (
                <div className="p-8 text-center text-gray-400">
                  No transaction history yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
